import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { API_BASE_URL } from '../config/api'
import { isShaanBanner, openShaanBridgePage } from '../utils/shaanBanner'

interface HeroBannerItem {
  id: number
  type?: 'image' | 'video'
  title: string
  redirect_url?: string
  is_active?: number | string
}

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [banners, setBanners] = useState<HeroBannerItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const location = useLocation()

  const isActive = (path: string) => location.pathname === path

  const navItems = [
    { path: '/', label: 'HOME' },
    { path: '/about-us', label: 'ABOUT US' },
    { path: '/events', label: 'EVENTS' },
    { path: '/gallery', label: 'GALLERY' },
    { path: '/contact', label: 'CONTACT' },
    { path: '/subscribe', label: 'SUBSCRIBE' },
  ]

  const fetchBanners = async (): Promise<HeroBannerItem[] | null> => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${API_BASE_URL}hero_list.php`)
      const result = await response.json()
      if (result && result.status && Array.isArray(result.data)) {
        const activeItems = result.data.filter(
          (item: HeroBannerItem) => Number(item.is_active ?? 1) === 1
        )
        setBanners(activeItems)
        return activeItems
      } else {
        setBanners([])
        return []
      }
    } catch (err) {
      console.error('Error fetching banner events:', err)
      setError('Failed to fetch events. Please try again.')
      return null
    } finally {
      setLoading(false)
    }
  }

  const handleRedirect = (redirectUrl?: string) => {
    if (redirectUrl) {
      window.open(redirectUrl, '_blank', 'noopener,noreferrer')
    }
  }

  const handleBannerSelect = (banner: HeroBannerItem) => {
    if (isShaanBanner(banner.title)) {
      setIsModalOpen(false)
      openShaanBridgePage()
    } else if (banner.redirect_url) {
      handleRedirect(banner.redirect_url)
    }
  }

  const handleBuyTicketsClick = async () => {
    setIsMenuOpen(false)
    const activeItems = await fetchBanners()
    if (activeItems && activeItems.length === 1) {
      handleBannerSelect(activeItems[0])
    } else {
      setIsModalOpen(true)
    }
  }

  const handleRetry = async () => {
    const activeItems = await fetchBanners()
    if (activeItems && activeItems.length === 1) {
      setIsModalOpen(false)
      handleBannerSelect(activeItems[0])
    }
  }

  return (
    <header className="bg-gradient-to-r from-white via-gray-50 to-gray-100 sticky top-0 z-50 shadow-lg border-b-2 border-primary-light/20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="flex items-center bg-white rounded-lg p-2 shadow-md group-hover:shadow-lg transition-shadow duration-300">
              <img
                src={`${import.meta.env.BASE_URL}lmt-logo-new.png`}
                alt="LMT SEVA Logo"
                className="h-12 w-auto object-contain"
              />
            </div>
            <div className="hidden sm:block">
              <div className="text-gray-900 font-bold text-lg group-hover:text-primary-light transition-colors duration-300">LMT SEVA</div>
              <div className="text-gray-600 text-xs">Ek Koshish, For Better Tomorrow</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative px-4 py-2 rounded-lg transition-colors duration-300 ${
                  isActive(item.path)
                    ? 'text-white font-semibold bg-primary-light shadow-md'
                    : 'text-gray-700 hover:text-primary-light hover:bg-primary-light/10 font-medium'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:block">
            <button
              className="bg-primary-light hover:bg-primary-dark text-white px-6 py-2 rounded-lg font-semibold transition-colors duration-300 shadow-md hover:shadow-lg"
              onClick={handleBuyTicketsClick}
            >
              Buy Tickets
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-gray-700 hover:text-primary-light"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isMenuOpen ? (
                <path d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-300">
            <nav className="flex flex-col space-y-4">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'text-white bg-primary-light font-semibold shadow-md'
                      : 'text-gray-700 hover:text-primary-light hover:bg-primary-light/10 font-medium'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <button
                className="mx-4 mt-4 bg-primary-light hover:bg-primary-dark text-white px-6 py-2 rounded-lg font-semibold transition-colors shadow-md"
                onClick={handleBuyTicketsClick}
              >
                Buy Tickets
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* Buy Tickets Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-opacity"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-gray-100 flex flex-col max-h-[85vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
              <h3 className="text-xl font-bold text-gray-900">Select Event</h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
                aria-label="Close modal"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto space-y-3 flex-1">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                  <div className="w-8 h-8 border-4 border-primary-light border-t-transparent rounded-full animate-spin mb-3"></div>
                  <p className="text-sm font-medium">Loading events...</p>
                </div>
              ) : error ? (
                <div className="text-center py-6">
                  <p className="text-red-500 font-medium mb-3">{error}</p>
                  <button
                    onClick={handleRetry}
                    className="px-4 py-2 bg-primary-light text-white text-sm font-semibold rounded-lg hover:bg-primary-dark transition-colors"
                  >
                    Retry
                  </button>
                </div>
              ) : banners.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="font-medium">No active events found.</p>
                </div>
              ) : (
                banners.map((banner) => (
                  <div
                    key={banner.id}
                    onClick={() => handleBannerSelect(banner)}
                    className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 hover:border-primary-light hover:shadow-lg transition-all duration-300 cursor-pointer group"
                  >
                    <span className="font-semibold text-gray-800 text-base md:text-lg group-hover:text-primary-light transition-colors pr-4">
                      {banner.title}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleBannerSelect(banner)
                      }}
                      aria-label={`Buy tickets for ${banner.title}`}
                      className="flex items-center justify-center w-9 h-9 rounded-full bg-primary-light/10 text-primary-light group-hover:bg-primary-light group-hover:text-white transition-all duration-300 flex-shrink-0"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  )
}

export default Header


